import classNames from 'classnames';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TASK_API_BASE, WBS_ELEMENT_BASE_URL } from '../../../../api/api';
import { useGenericAPI } from '../../../../api/useGenericApi';
import { ProCol } from '../../../../atoms/Grid/Col';
import { ProRow } from '../../../../atoms/Grid/Row';
import { ProValSwitch } from '../../../../atoms/TwoValSwitch/TwoValSwitch';
import { ApplicationElementType, getBaseBCItemForType } from '../../../../components/DetailWindow/breadCrump.helpers';
import { ProPage } from '../../../../components/Page/Page';
import { ProjectContext } from '../../../../contexts/ProjectContext';
import { UserContext } from '../../../../contexts/UserContext';
import { Components } from '../../../../utils/types';
import {
    RequirementDetailWindowType,
    useRequirementDetailsFrame,
} from '../../Requirements/modules/RequirementDetailsFrame';
import { useTaskDetailsWindowForRequirements } from '../../Tasks/components/useTaskDetailsWindowForRequirements';
import { DndContext, DragOverEvent } from '@dnd-kit/core';
import { DailyTaskRow } from './modules/DailyTaskRow';
import { stat } from 'fs';

export const DaiyCeremonyPageTaskBoard = ({
    selectedSprint,
    toggleView,
}: {
    selectedSprint: Components.Schemas.WBSElementDeltaDTO;
    toggleView: () => void;
}) => {
    const { t } = useTranslation();
    const { selectedTeam } = useContext(ProjectContext);
    const { portfolio } = useContext(UserContext);
    const [selectedTask, setSelectedTask] = useState<Components.Schemas.TaskDTO>();
    const [selectedRequirement, setSelectedRequirement] = useState<Components.Schemas.RequirementDTO>();
    const taskStates = useMemo(() => portfolio?.taskCompletionStates, [portfolio]);

    const {
        data: sprint,
        error,
        loading,
        sync,
    } = useGenericAPI<Components.Schemas.WBSElementDTO>(WBS_ELEMENT_BASE_URL, selectedSprint?.id);

    const { updateRequest } = useGenericAPI<Components.Schemas.TaskDTO>(TASK_API_BASE, selectedTask?.id);

    const deselectTableRow = () => {
        setSelectedRequirement(undefined);
        setSelectedTask(undefined);
        open('hidden');
        openTask('hidden');
    };

    const selectStory = (id?: string) => {
        const req = sprint?.requirements?.find((cur) => cur.id === id);
        if (req) {
            open('view');
            setSelectedRequirement(req);
        }
    };

    const addSubtask = (req: Components.Schemas.RequirementDTO) => {
        setSelectedRequirement(req);
        setSelectedTask(undefined);
        open('hidden');
        openTask('new');
    };

    const selectTask = (task: Components.Schemas.TaskDTO) => {
        setSelectedTask(task);
        open('hidden');
        setSelectedRequirement(undefined);
        openTask('view');
    };

    const { open, renderDetailWindow, isDisplayed } = useRequirementDetailsFrame({
        closeAction: deselectTableRow,
        id: selectedRequirement?.id,
        selectAction: selectStory,
        externalSync: sync,
        type: RequirementDetailWindowType.Daily,
    });

    const {
        isDisplayed: taskIsDisplayed,
        mode: taskMode,
        open: openTask,
        renderDetailWindow: renderTaskDetailWindow,
    } = useTaskDetailsWindowForRequirements({
        closeAction: deselectTableRow,
        selectAction: selectStory,
        externalSync: sync,
        id: selectedTask?.id,
        selectedRequirement: selectedRequirement,
        type: RequirementDetailWindowType.Daily,
    });

    function handleDragEnd({ over, active }: DragOverEvent) {
        const allTasks = sprint?.requirements
            ?.map((cur) => cur.taskLists)
            .flat()
            .map((cur) => cur?.tasks)
            .flat();
        const state = taskStates?.find((cur) => over?.id.toString().includes(cur.taskCompletionStateType ?? '...'));
        const task = allTasks?.find((cur) => cur?.id === active.id);

        if (task?.taskCompletionState?.taskCompletionStateType !== state?.taskCompletionStateType)
            updateRequest({ ...task, taskCompletionState: state }).then(async () => sync());
    }

    return (
        <DndContext onDragEnd={handleDragEnd}>
            <ProPage
                breadCrump={getBaseBCItemForType(
                    [
                        ApplicationElementType.TeamManagement,
                        { type: ApplicationElementType.TeamManagement, name: selectedTeam?.name },
                    ],
                    t
                )}
                name={'Daily standup / Processing: Tasks'}
                loading={loading}
                error={error}
                filter={<ProValSwitch left="Story board" right="Task board" toggle={toggleView} aktive="right" />}
            >
                <div className="TaskContainer">
                    <ProRow className="TaskContainer__statusRow">
                        <ProCol span={4}>
                            <h3>Not started</h3>
                        </ProCol>
                        <ProCol span={4}>
                            <h3>In progress</h3>
                        </ProCol>
                        <ProCol span={4}>
                            <h3>Completed</h3>
                        </ProCol>
                    </ProRow>
                    {sprint?.requirements?.map((cur) => (
                        <DailyTaskRow
                            addSubtask={addSubtask}
                            req={cur}
                            selectStory={selectStory}
                            selectTask={selectTask}
                            key={`task-row${cur.id}`}
                        />
                    ))}
                </div>

                <div
                    className={classNames(
                        'ProApp--detailWindowSection',
                        (isDisplayed || taskIsDisplayed) && 'ProApp--detailWindowSection__expanded'
                    )}
                >
                    {renderDetailWindow()}
                    {renderTaskDetailWindow()}
                </div>
            </ProPage>
        </DndContext>
    );
};
